import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

import type { BaseQuery } from './baseQuery.types';

type GetUsersResponse = components['schemas']['GetUsersEndpoint_GetUsersPagedResponse'];

export interface UseUsersReturn extends BaseQuery {
  users: GetUsersResponse | null;
}

export function useUsers(page: number, pageSize: number, sortedBy: string | null, filter: string | null = null): UseUsersReturn {
  const { organizationId } = useOrganizationId();
  const { getAccessToken } = useAuth();

  const getUsers = async (
    organizationId: number,
    page: number,
    pageSize: number,
    sortedBy: string | null,
    filter: string | null = null,
  ) => {
    const client = createApiClient(getAccessToken);
    const { data } = await client.GET('/api/organizations/{organizationId}/users', {
      params: {
        path: { organizationId },
        query: {
          Page: page,
          PageSize: pageSize,
          ...(sortedBy ? { SortedBy: sortedBy } : {}),
          ...(filter ? { Filter: filter } : {}),
        },
      },
    });

    if (!data) {
      throw new Error('No data returned from API');
    }

    return data as GetUsersResponse;
  };

  const useUsers = useQuery({
    queryKey: ['users', organizationId, page, pageSize, sortedBy, filter],
    queryFn: () => getUsers(organizationId, page, pageSize, sortedBy, filter),
    retry: false,
  });

  const { data, isLoading, isPending, error, refetch, isRefetching } = useUsers;

  return {
    users: data ?? null,
    isLoading: isLoading || isPending,
    isRefetching,
    error: error as Error | null,
    refetch,
  };
}
